// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import type {
  BrandSpecial,
  MaterializedMenuProduct,
  StoreSpecial as V3StoreSpecial,
} from '@iheartjane/data-access';
import { JaneGoldTag, getApplicableGoldTagLabel } from '@iheartjane/menu';
import { Card, Flex, Typography } from '@iheartjane/reefer';
import type { PriceId } from '@iheartjane/types';
import { formatCurrency } from '@iheartjane/utils';
import { useCallback } from 'react';

import { useGetProductPrices, useKioskSpacing } from '@jane/kiosk/hooks';
import type { AlgoliaProduct } from '@jane/search/data-access';
import type { StoreSpecial } from '@jane/shared/types';
import {
  availableWeightsForMenuProduct,
  labelForWeightId,
} from '@jane/shared/util';

import { Grid } from '../Grid/Grid';

const StyledJaneGoldTag = styled(JaneGoldTag)({
  whiteSpace: 'nowrap',
});

type WeightFilter = (
  weight: PriceId,
  product: AlgoliaProduct | MaterializedMenuProduct
) => boolean;

export interface ProductWeightsGridProps {
  numColumns?: number;
  onWeightClick: (weight: PriceId) => void;
  product: AlgoliaProduct | MaterializedMenuProduct;
  selectedWeight: PriceId;
  shouldShowGold: boolean;
  specials: (StoreSpecial | V3StoreSpecial)[];
  visiblePrideIds?: PriceId[];
  weightFilter?: WeightFilter;
  weightRuleBrandSpecial?: BrandSpecial[];
}

export const ProductWeightsGrid = ({
  numColumns = 3,
  onWeightClick,
  product,
  selectedWeight,
  shouldShowGold,
  specials,
  visiblePrideIds,
  weightFilter,
  weightRuleBrandSpecial = [],
}: ProductWeightsGridProps) => {
  const baseWeights = availableWeightsForMenuProduct(product, visiblePrideIds);
  const weights = weightFilter
    ? baseWeights.filter((weight) => weightFilter(weight, product))
    : baseWeights;

  const { defaultSpacing } = useKioskSpacing();

  const getProductPrice = useGetProductPrices(product, specials);

  const renderWeight = useCallback(
    (weight: PriceId) => {
      const weightLabel = labelForWeightId(weight, product.amount);
      const weightPrice = formatCurrency(getProductPrice(weight).price);
      const isSelected = selectedWeight === weight;

      // Show the Jane Gold pill only on weights the special actually applies to.
      // `getApplicableGoldTagLabel` returns null for weights outside the
      // special's weight rule, which is the single source of truth here.
      const janeGoldTagLabel = shouldShowGold
        ? getApplicableGoldTagLabel({
            brandSpecials: weightRuleBrandSpecial,
            applicableBrandSpecialIds: product.applicable_brand_special_ids,
            priceId: weight,
          })
        : null;

      return (
        <Card
          ariaLabel={`select ${weightLabel} for ${weightPrice}`}
          border="grays-light"
          flat
          onClick={() => onWeightClick(weight)}
          key={weight}
        >
          <Card.Content background={isSelected ? 'grays-black' : 'grays-white'}>
            <Flex
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              grow={1}
              py={16}
              gap={8}
              width="100%"
              height="100%"
            >
              <Typography
                color={isSelected ? 'grays-white' : 'grays-black'}
                variant="body-semibold"
              >{`${weightPrice} / ${weightLabel}`}</Typography>
              {janeGoldTagLabel && (
                <StyledJaneGoldTag
                  label={janeGoldTagLabel}
                  data-testid={`jane-gold-tag-${weight}`}
                />
              )}
            </Flex>
          </Card.Content>
        </Card>
      );
    },
    [
      getProductPrice,
      onWeightClick,
      product,
      selectedWeight,
      shouldShowGold,
      weightRuleBrandSpecial,
    ]
  );

  return (
    <Grid
      id="weights-grid"
      gap={16}
      gridTemplateColumns={`repeat(auto-fill, minmax(calc(${Math.floor(
        100 / numColumns
      )}% - ${defaultSpacing}px), 1fr))`}
      style={{ position: 'relative', width: '100%' }}
    >
      {weights.map(renderWeight)}
    </Grid>
  );
};
